import React, { useEffect, useState, useCallback, useContext } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { ServiceStatus, ServiceStep } from "../../../utilities/types/unified-services";
import ServiceErrorTemplate from "../ServiceErrorTemplate";
import AppContext from "../../../utilities/contexts/appContext";
import SdkContext from "../../../utilities/contexts/sdk/sdkContext";
import { useLocation } from "react-router-dom";
import GarmentMeasurementProcessing from "./GarmentMeasurementProcessing";
import { addRefIdToPayload } from "../../../utilities/helpers/refIdHelper";

const UnifiedProcessingStep: React.FC = () => {
  const { serviceInterface, serviceConfig, bodyMeasurement, autoTagging, visualSearch, resetAll } = useUnifiedService();
  const { status, setStatus, setCanProceedToNextStep, setCurrentStep } = serviceInterface;
  const appContext = useContext(AppContext);
  const sdkContext = useContext(SdkContext);
  const location = useLocation();

  const isSDK = location.pathname.includes("/sdk/");
  // If requestId exists, always use SDK Token (client-specific request)
  // Otherwise: Main Token (if logged in) > SDK Token (fallback)
  const authToken = isSDK ? (sdkContext.requestId ? sdkContext.token : appContext.token || sdkContext.token) : appContext.token;

  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("Preparing your request...");
  const [apiError, setApiError] = useState<{ message: string; type: "NETWORK" | "API" } | null>(null);

  const hasRequiredData = useCallback(() => {
    switch (serviceConfig.serviceId) {
      case "body-measurement":
        const { frontImage, sideImage, height } = bodyMeasurement;
        const bodyResult = frontImage.isValid && sideImage.isValid && frontImage.base64 && sideImage.base64 && height;
        return bodyResult;

      case "auto-tagging":
        const { image } = autoTagging;
        const autoResult = image.isValid && image.base64;
        return autoResult;

      case "visual-search":
        const { image: visualImage } = visualSearch;
        const visualResult = visualImage.isValid && visualImage.base64;
        return visualResult;

      default:
        return false;
    }
  }, [serviceConfig.serviceId, bodyMeasurement, autoTagging, visualSearch]);

  const callServiceAPI = useCallback(async () => {
    let payload: any;
    let setResultData: (data: any) => void;
    let taskMessage: string;

    switch (serviceConfig.serviceId) {
      case "body-measurement":
        const { height, setMeasurementData, getImagesForService } = bodyMeasurement;
        const serviceImages = getImagesForService();
        payload = addRefIdToPayload({
          present_height: height!.toString(),
          img_full_view_body: serviceImages.frontImage!,
          img_side_view_body: serviceImages.sideImage!,
          ...(isSDK && sdkContext.requestId && { request_id: sdkContext.requestId }),
        });
        setResultData = setMeasurementData;
        taskMessage = "Processing your measurements...";
        break;
      case "auto-tagging":
        const { image, setResult } = autoTagging;
        payload = addRefIdToPayload({
          image: image.base64!,
        });
        setResultData = setResult;
        taskMessage = "Analyzing clothing items...";
        break;
      case "visual-search":
        const { image: visualImage, setRecognizedClothing, setIsFirstServiceCalled } = visualSearch;
        payload = addRefIdToPayload({
          image: visualImage.base64!,
        });
        setResultData = (data: any) => {
          setRecognizedClothing(data);
          setIsFirstServiceCalled(true);
        };
        taskMessage = "Recognizing clothing items...";
        break;

      default:
        throw new Error(`Unsupported service: ${serviceConfig.serviceId}`);
    }

    setCurrentTask(taskMessage);

    const response = await fetch(serviceConfig.api.endpoint, {
      method: serviceConfig.api.method,
      headers: {
        "Content-Type": "application/json",
        ...(serviceConfig.api.requiresAuth && {
          Authorization: `Bearer ${authToken || ""}`,
        }),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorMessage = "A problem occurred while processing your request.";

      switch (response.status) {
        case 400:
          errorMessage = "Invalid request data. Please check your inputs and try again.";
          break;
        case 401:
          errorMessage = "Authentication required. Please log in and try again.";
          break;
        case 403:
          errorMessage = "Access denied. You don't have permission to use this service.";
          break;
        case 404:
          errorMessage = "Service not found. Please try again later.";
          break;
        case 429:
          errorMessage = "Too many requests. Please wait a moment and try again.";
          break;
        case 500:
          errorMessage = "Server error. Please try again later.";
          break;
        case 502:
        case 503:
        case 504:
          errorMessage = "Service temporarily unavailable. Please try again later.";
          break;
        default:
          errorMessage = `Server returned error ${response.status}. Please try again.`;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data.ok || data.status_code !== 200) {
      throw new Error(data.message || "Service returned an error");
    }

    let processedData;
    switch (serviceConfig.serviceId) {
      case "body-measurement":
        if (!data.data?.result) {
          throw new Error("Invalid response format from service");
        }
        processedData = data.data.result;
        break;
      case "auto-tagging":
        if (!data.data?.result) {
          throw new Error("Invalid response format from service");
        }
        if (!data.data?.result.Clothes.length) {
          throw new Error("we couldn't detect any clothing items in the image. Please try another image.");
        }
        processedData = data.data.result;
        break;

      case "visual-search":
        if (!data.data?.result) {
          throw new Error("Invalid response format from service");
        }
        // if (!data.data?.result.Clothes.length) {
        //   throw new Error("we couldn't detect any clothing items in the image. Please try another image.");
        // }
        processedData = data.data.result;
        break;

      default:
        processedData = data.data?.result || data.data;
    }

    setResultData(processedData);

    return data;
  }, [
    serviceConfig.serviceId,
    serviceConfig.api.endpoint,
    serviceConfig.api.method,
    serviceConfig.api.requiresAuth,
    authToken,
    bodyMeasurement,
    autoTagging,
    visualSearch,
    isSDK,
    sdkContext.requestId,
  ]);

  const processService = useCallback(async () => {
    try {
      setStatus(ServiceStatus.LOADING);
      setProgress(0);

      await callServiceAPI();

      setProgress(100);
      setCurrentTask("Complete!");
      setStatus(ServiceStatus.SUCCESS);
      setCanProceedToNextStep(true);
    } catch (error: any) {
      console.error("Processing error:", error);

      const isNetworkError = error.name === "TypeError" || error.message.includes("fetch");

      setApiError({
        message: error.message || "An unexpected error occurred",
        type: isNetworkError ? "NETWORK" : "API",
      });

      setStatus(ServiceStatus.ERROR);
    }
  }, [callServiceAPI]);

  const handleRetry = () => {
    setApiError(null);
    setProgress(0);
    setCurrentTask("");
    processService();
  };

  const handleGoBack = () => {
    // Reset all service states and go back to GUIDE step
    resetAll();
  };

  useEffect(() => {
    if (serviceConfig.serviceId === "garment-measurement") {
      return;
    }

    if ((status === ServiceStatus.IDLE || status === ServiceStatus.SUCCESS) && hasRequiredData()) {
      processService();
    }
  }, []);

  useEffect(() => {
    if (serviceConfig.serviceId === "garment-measurement") {
      return;
    }

    if (status === ServiceStatus.SUCCESS) {
      const timer = setTimeout(() => {
        setCurrentStep(ServiceStep.RESULTS);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [status, serviceConfig.serviceId]);

  if (serviceConfig.serviceId === "garment-measurement") {
    return <GarmentMeasurementProcessing />;
  }

  if (status === ServiceStatus.ERROR && apiError) {
    return (
      <ServiceErrorTemplate
        title={apiError.type === "NETWORK" ? "Connection Problem" : "Service Error"}
        message={apiError.message}
        type={apiError.type}
        onRetry={handleRetry}
        onGoBack={handleGoBack}
        showRetry={true}
        showGoBack={true}
      />
    );
  }

  return (
    <div className="flex flex-col items-center w-full gap-4">
      <div className="mb-6 text-center">
        <h2 className="mb-1 text-2xl font-medium text-secondary">{serviceConfig.serviceId === "body-measurement" ? "Processing Your Measurements" : "Processing Your Request"}</h2>
        <p className="text-gray-600">Please wait while we analyze your data</p>
      </div>

      <div className="w-full max-w-md p-8 bg-white border border-gray-200 shadow-sm rounded-xl">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-24 h-24">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />

              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="url(#progressGradient)"
                strokeWidth="8"
                fill="none"
                strokeDasharray="251.2"
                strokeDashoffset={251.2 - (progress / 100) * 251.2}
                strokeLinecap="round"
                className="transition-all duration-300 ease-out"
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#1d4ed8" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-semibold text-primary">{progress}%</span>
            </div>
          </div>

          <div className="text-center">
            <h3 className="mb-2 text-lg font-medium text-secondary">{currentTask}</h3>
            <p className="text-sm text-gray-600">This process may take a few moments</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedProcessingStep;
